import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { getData } from "country-list";
import ReactCountryFlag from "react-country-flag";
import { Calendar } from "primereact/calendar";
import {
  formatDateToISOStartOfDay,
  removeTimezoneFromDateString,
} from "~/utils/dateUtil";

const ShareholderInformationForm = ({
  setBtnToggle,
  formDetails,
  setFormDetails,
  regData,
  setRegData,
  isEditing,
  setFormResetRef,
  shareholderType,
  statusOptions,
  legalStructureOptions,
  resolveValue,
}) => {
  const countries = getData();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: regData?.name || "",
      last_name: regData?.last_name || "",
      preferred_name: regData?.preferred_name || "",
      other_name: regData?.other_name || "",
      incorporation_country:
        resolveValue(regData?.incorporation_country, countries) || "",
      company_registration_no: regData?.company_registration_no || "",
      legal_name: regData?.legal_name || "",
      legal_structure:
        resolveValue(regData?.legal_structure, legalStructureOptions) || "",
      status: resolveValue(regData?.status, statusOptions) || "",
      nationality: resolveValue(regData?.nationality, countries) || "",
      date_of_birth: regData?.date_of_birth || "",
      email: regData?.email || "",
      phone_code: regData?.phone_code || "",
      phone: regData?.phone || "",
      point_of_contact: regData?.point_of_contact || "",
      contact_email: regData?.contact_email || "",
      tax_identification_no: regData?.tax_identification_no || "",
      local_tax_id: regData?.local_tax_id || "",
    },
  });

  useEffect(() => {
    setBtnToggle(!isValid);
  }, [isValid, setBtnToggle]);

  const formDetailsRef = useRef(formDetails);
  useEffect(() => {
    if (
      JSON.stringify(formDetailsRef.current) !== JSON.stringify(formDetails)
    ) {
      reset(formDetails);
      formDetailsRef.current = formDetails;
    }
  }, [formDetails, reset]);

  useEffect(() => {
    if (setFormResetRef) {
      setFormResetRef((data) => {
        reset(data || formDetailsRef.current);
        formDetailsRef.current = data || formDetailsRef.current;
      });
    }
  }, [setFormResetRef, reset]);

  useEffect(() => {
    const subscription = watch((value) => {
      if (JSON.stringify(formDetailsRef.current) !== JSON.stringify(value)) {
        formDetailsRef.current = value;
        setFormDetails(value);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setFormDetails]);

  useEffect(() => {
    if (regData) {
      reset({
        name: regData?.name || "",
        last_name: regData?.last_name || "",
        preferred_name: regData?.preferred_name || "",
        other_name: regData?.other_name || "",
        incorporation_country:
          resolveValue(regData?.incorporation_country, countries) || "",
        company_registration_no: regData?.company_registration_no || "",
        legal_name: regData?.legal_name || "",
        legal_structure:
          resolveValue(regData?.legal_structure, legalStructureOptions) || "",
        status: resolveValue(regData?.status, statusOptions) || "",
        nationality: resolveValue(regData?.nationality, countries) || "",
        date_of_birth: regData?.date_of_birth || "",
        email: regData?.email || "",
        phone_code: regData?.phone_code || "",
        phone: regData?.phone || "",
        point_of_contact: regData?.point_of_contact || "",
        contact_email: regData?.contact_email || "",
        tax_identification_no: regData?.tax_identification_no || "",
        local_tax_id: regData?.local_tax_id || "",
      });
    }
  }, [regData, reset]);

  const onSubmit = (data) => {
    setFormDetails(data);
    setRegData(data);
  };

  const statusOptionsTemplate = (option) => {
    return (
      <div className="flex align-items-center gap-2">
        <RadioButton
          checked={
            typeof regData?.status === "object"
              ? regData?.status?.code === option?.code
              : regData?.status === option?.code
          }
        />
        <span className={`pi pi-circle-fill ${option.colorClass}`}></span>
        <span>{option.name}</span>
      </div>
    );
  };

  const legalStructureTemplate = (option) => {
    return (
      <div className="flex align-items-center gap-2">
        <RadioButton
          checked={
            typeof regData?.legal_structure === "object"
              ? regData?.legal_structure?.code === option?.code
              : regData?.legal_structure === option?.code
          }
        />
        <span>{option.name}</span>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="formgrid grid">
        {shareholderType && (
          <>
            <div className="field col-12">
              <p className="current-position-head mt-2">General Information</p>
            </div>

            {shareholderType === "Individual" && (
              <>
                <div className="field col-6">
                  <label className="contact-label mt-2">
                    Given name(s) *{" "}
                    <span
                      id="given-name-info"
                      className="pi pi-info-circle"
                    ></span>
                    <Tooltip
                      target="#given-name-info"
                      content="This is usually the person first name or the name they usually go by."
                      position="right"
                      className="custom-tooltip tooltip-custom-format"
                    />
                  </label>
                  <Controller
                    name="name"
                    control={control}
                    defaultValues
                    rules={{
                      required: "Given Name is required.",
                      pattern: {
                        value: /^[A-Za-z\s]+$/,
                        message: "Special characters are not allowed.",
                      },
                    }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({ ...regData, name: newValue });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Enter given name(s)"
                        className={`input-field appearance-none outline-none w-full ${
                          errors.name ? "error" : ""
                        }`}
                        disabled={!isEditing}
                      />
                    )}
                  />
                  {errors.name && (
                    <small className="error-message">
                      {errors.name.message}
                    </small>
                  )}
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">
                    Other Name(s)
                    <span
                      id="other-name-info"
                      className="pi pi-info-circle info-circle-span"
                    ></span>
                    <Tooltip
                      target="#other-name-info"
                      content="Any middle or extra names that the employee has. You can leave this blank if it doesn’t apply."
                      position="right"
                      className="tooltip-custom-format"
                    />
                  </label>
                  <Controller
                    name="other_name"
                    control={control}
                    defaultValues
                    rules={{
                      pattern: {
                        value: /^[A-Za-z\s]+$/,
                        message: "Special characters are not allowed.",
                      },
                    }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({ ...regData, other_name: newValue });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Enter any other names, such as middle names or clan names"
                        className={`input-field appearance-none outline-none w-full ${
                          errors.name ? "error" : ""
                        }`}
                        disabled={!isEditing}
                      />
                    )}
                  />
                  {errors.other_name && (
                    <small className="error-message">
                      {errors.other_name.message}
                    </small>
                  )}
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">
                    Surname(s)
                    <span
                      id="surname-info"
                      className="pi pi-info-circle info-circle-span"
                    ></span>
                    <Tooltip
                      target="#surname-info"
                      content="This is usually a family name or last name. If employee has more than one, include them here."
                      position="right"
                      className="custom-tooltip tooltip-custom-format"
                    />
                  </label>
                  <Controller
                    name="last_name"
                    control={control}
                    defaultValues
                    rules={{
                      pattern: {
                        value: /^[A-Za-z\s]+$/,
                        message: "Special characters are not allowed.",
                      },
                    }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({ ...regData, last_name: newValue });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Enter surname(s)."
                        className={`input-field appearance-none outline-none w-full ${
                          errors.name ? "error" : ""
                        }`}
                        disabled={!isEditing}
                      />
                    )}
                  />
                  {errors.last_name && (
                    <small className="error-message">
                      {errors.last_name.message}
                    </small>
                  )}
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">
                    Preferred Name(s)
                    <span
                      id="preferred-name-info"
                      className="pi pi-info-circle info-circle-span"
                    ></span>
                    <Tooltip
                      target="#preferred-name-info"
                      content="If the employee wants to be called by a different name than their given name, put that here."
                      position="right"
                      className="custom-tooltip tooltip-custom-format"
                    />
                  </label>
                  <Controller
                    name="preferred_name"
                    control={control}
                    defaultValues
                    rules={{
                      pattern: {
                        value: /^[A-Za-z\s]+$/,
                        message: "Special characters are not allowed.",
                      },
                    }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({
                            ...regData,
                            preferred_name: newValue,
                          });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Preferred name or nickname"
                        className={`input-field appearance-none outline-none w-full ${
                          errors.preferred_name ? "error" : ""
                        }`}
                        disabled={!isEditing}
                      />
                    )}
                  />

                  {errors.preferred_name && (
                    <small className="error-message">
                      {errors.preferred_name.message}
                    </small>
                  )}
                </div>
              </>
            )}

            {shareholderType === "Corporate" && (
              <>
                <div className="field col-6">
                  <label className="contact-label mt-2">
                    Country of Incorporation
                  </label>
                  <Controller
                    name="incorporation_country"
                    control={control}
                    render={({ field }) => {
                      const countryObject =
                        typeof regData?.incorporation_country === "object"
                          ? regData?.incorporation_country
                          : countries?.find(
                              (country) =>
                                country.code === regData?.incorporation_country,
                            );
                      const isDisabled = !countries.length;
                      return (
                        <Dropdown
                          {...field}
                          value={countryObject || field.value}
                          onChange={(e) => {
                            const newValue = e.value;
                            setRegData({
                              ...regData,
                              incorporation_country: newValue,
                            });
                            field.onChange(newValue);
                          }}
                          options={
                            countryObject && !countries.length
                              ? [countryObject]
                              : countries.length
                                ? countries
                                : [countryObject].filter(Boolean)
                          }
                          optionLabel="name"
                          placeholder="Select country"
                          className={`officer-dropdown-field w-full`}
                          disabled={isDisabled || !isEditing}
                          itemTemplate={(option) => (
                            <div className="flex align-items-center">
                              <ReactCountryFlag
                                countryCode={option.code}
                                svg
                                className="flag-svg-icon"
                              />
                              <span>{option.name}</span>
                            </div>
                          )}
                          valueTemplate={(option) => {
                            return option ? (
                              <div className="flex align-items-center">
                                <ReactCountryFlag
                                  countryCode={option.code}
                                  svg
                                  className="flag-svg-icon"
                                />
                                <span>{option.name}</span>
                              </div>
                            ) : (
                              "Select country"
                            );
                          }}
                        />
                      );
                    }}
                  />
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">
                    Company registration number
                  </label>
                  <Controller
                    name="company_registration_no"
                    control={control}
                    defaultValues
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({
                            ...regData,
                            company_registration_no: newValue,
                          });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Enter registration Number"
                        disabled={!isEditing}
                        className={`input-field appearance-none outline-none w-full`}
                      />
                    )}
                  />
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">Legal Name *</label>
                  <Controller
                    name="legal_name"
                    control={control}
                    defaultValues
                    rules={{
                      required: "Legal Name is required.",
                    }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({ ...regData, legal_name: newValue });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Enter legal name"
                        disabled={!isEditing}
                        className={`input-field appearance-none outline-none w-full ${
                          errors.legal_name ? "error" : ""
                        }`}
                      />
                    )}
                  />
                  {errors.legal_name && (
                    <small className="error-message">
                      {errors.legal_name.message}
                    </small>
                  )}
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">Legal Structure</label>
                  <Controller
                    name="legal_structure"
                    control={control}
                    render={({ field }) => {
                      const currentLegalStructure = regData?.legal_structure;
                      const legalStructureObject =
                        typeof regData?.legal_structure === "object"
                          ? regData?.legal_structure
                          : legalStructureOptions?.find(
                              (status) => status.code === currentLegalStructure,
                            );
                      const isDisabled = !legalStructureOptions.length;
                      return (
                        <Dropdown
                          {...field}
                          value={legalStructureObject || field.value}
                          onChange={(e) => {
                            const newValue = e.value;
                            const updatedLegalStructure = newValue.code;
                            setRegData({
                              ...regData,
                              legal_structure: newValue,
                            });
                            field.onChange(updatedLegalStructure);
                          }}
                          options={legalStructureOptions}
                          optionLabel="name"
                          itemTemplate={legalStructureTemplate}
                          placeholder="Select legal structure"
                          disabled={isDisabled || !isEditing}
                          className={`officer-dropdown-field w-full`}
                        />
                      );
                    }}
                  />
                </div>
              </>
            )}

            <div className="field col-12">
              <label className="contact-label mt-2">Status</label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => {
                  const currentStatus = regData?.status;
                  const statusObject =
                    typeof regData?.status === "object"
                      ? regData?.status
                      : statusOptions?.find(
                          (status) => status.code === currentStatus,
                        );
                  const isDisabled = !statusOptions.length;
                  return (
                    <Dropdown
                      {...field}
                      value={statusObject || field.value}
                      onChange={(e) => {
                        const newValue = e.value;
                        const updatedStatus = newValue.code;
                        setRegData({ ...regData, status: newValue });
                        field.onChange(updatedStatus);
                      }}
                      options={statusOptions}
                      optionLabel="name"
                      placeholder="Select status"
                      itemTemplate={statusOptionsTemplate}
                      disabled={isDisabled || !isEditing}
                      className={`officer-dropdown-field w-full`}
                    />
                  );
                }}
              />
            </div>

            {shareholderType === "Individual" && (
              <>
                <div className="field col-6">
                  <label className="contact-label mt-2">Nationality</label>
                  <Controller
                    name="nationality"
                    control={control}
                    render={({ field }) => {
                      const countryObject =
                        typeof regData?.nationality === "object"
                          ? regData?.nationality
                          : countries?.find(
                              (country) =>
                                country.code === regData?.nationality,
                            );
                      const isDisabled = !countries.length;
                      return (
                        <Dropdown
                          {...field}
                          value={countryObject || field.value}
                          onChange={(e) => {
                            const newValue = e.value;
                            setRegData({ ...regData, nationality: newValue });
                            field.onChange(newValue);
                          }}
                          options={
                            countryObject && !countries.length
                              ? [countryObject]
                              : countries.length
                                ? countries
                                : [countryObject].filter(Boolean)
                          }
                          optionLabel="name"
                          placeholder="Select nationality"
                          className={`officer-dropdown-field w-full`}
                          disabled={isDisabled || !isEditing}
                          itemTemplate={(option) => (
                            <div className="flex align-items-center">
                              <ReactCountryFlag
                                countryCode={option.code}
                                svg
                                className="flag-svg-icon"
                              />
                              <span>{option.name}</span>
                            </div>
                          )}
                          valueTemplate={(option) => {
                            return option ? (
                              <div className="flex align-items-center">
                                <ReactCountryFlag
                                  countryCode={option.code}
                                  svg
                                  className="flag-svg-icon"
                                />
                                <span>{option.name}</span>
                              </div>
                            ) : (
                              "Select nationality"
                            );
                          }}
                        />
                      );
                    }}
                  />
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">Date of birth</label>
                  <div className="calendar-container">
                    <Controller
                      name="date_of_birth"
                      control={control}
                      defaultValues
                      render={({ field }) => (
                        <>
                          <Calendar
                            {...field}
                            value={
                              field.value &&
                              removeTimezoneFromDateString(field.value)
                            }
                            onChange={(e) => {
                              const newDate = formatDateToISOStartOfDay(
                                e.value,
                              );
                              setRegData({
                                ...regData,
                                date_of_birth: newDate,
                              });
                              field.onChange(newDate);
                            }}
                            placeholder="DD/MM/YY"
                            maxDate={new Date()}
                            disabled={!isEditing}
                            className="calendar-field w-full"
                          />
                          <span className="pi pi-calendar calendar-icon"></span>
                        </>
                      )}
                    />
                  </div>
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">Contact email *</label>
                  <Controller
                    name="email"
                    control={control}
                    defaultValues
                    rules={{
                      required: "Email is required.",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/,
                        message: "Email is invalid.",
                      },
                    }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({ ...regData, email: newValue });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Enter email address"
                        disabled={!isEditing}
                        className={`input-field appearance-none outline-none w-full ${
                          errors.email ? "error" : ""
                        }`}
                      />
                    )}
                  />
                  {errors.email && (
                    <small className="error-message">
                      {errors.email.message}
                    </small>
                  )}
                </div>
              </>
            )}

            <div className="field col-6 mb-0">
              <label className="contact-label mt-2">
                Phone Number {shareholderType === "Individual" && "*"}
              </label>
              <div className="inline-flex w-full">
                <div className="field col-4 pl-0">
                  <Controller
                    name="phone_code"
                    control={control}
                    defaultValues
                    rules={{
                      ...(shareholderType === "Individual"
                        ? { required: "Country code is required." }
                        : {}),
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Country code must contain only digits.",
                      },
                    }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({ ...regData, phone_code: newValue });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Country code"
                        disabled={!isEditing}
                        className={`input-field appearance-none outline-none w-full ${
                          errors.phone_code ? "error" : ""
                        }`}
                      />
                    )}
                  />
                  {errors.phone_code && (
                    <small className="error-message">
                      {errors.phone_code.message}
                    </small>
                  )}
                </div>
                <div className="field col-8 pr-0">
                  <Controller
                    name="phone"
                    control={control}
                    defaultValues
                    rules={{
                      ...(shareholderType === "Individual"
                        ? { required: "Phone number is required." }
                        : {}),
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Phone number must contain only digits.",
                      },
                    }}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({ ...regData, phone: newValue });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Enter phone number"
                        disabled={!isEditing}
                        className={`input-field appearance-none outline-none w-full ${
                          errors.phone ? "error" : ""
                        }`}
                      />
                    )}
                  />
                  {errors.phone && (
                    <small className="error-message">
                      {errors.phone.message}
                    </small>
                  )}
                </div>
              </div>
            </div>

            {shareholderType === "Corporate" && (
              <div className="field col-12">
                <label className="contact-label mt-2">Contact email</label>
                <Controller
                  name="email"
                  control={control}
                  defaultValues
                  rules={{
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/,
                      message: "Email is invalid.",
                    },
                  }}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      type="text"
                      value={field.value}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setRegData({ ...regData, email: newValue });
                        field.onChange(newValue);
                      }}
                      placeholder="Enter email address"
                      disabled={!isEditing}
                      className={`input-field appearance-none outline-none w-full ${
                        errors.email ? "error" : ""
                      }`}
                    />
                  )}
                />
                {errors.email && (
                  <small className="error-message">
                    {errors.email.message}
                  </small>
                )}
              </div>
            )}

            <div className="field col-12">
              <p className="current-position-head mt-2">Point of Contact</p>
            </div>

            <div className="field col-6">
              <label className="contact-label mt-2">
                Contact full name {shareholderType === "Individual" && "*"}
              </label>
              <Controller
                name="point_of_contact"
                control={control}
                defaultValues
                rules={{
                  ...(shareholderType === "Individual"
                    ? { required: "Full Name is required." }
                    : {}),
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: "Special characters are not allowed.",
                  },
                }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    value={field.value}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setRegData({ ...regData, point_of_contact: newValue });
                      field.onChange(newValue);
                    }}
                    type="text"
                    placeholder="Enter full name"
                    disabled={!isEditing}
                    className={`input-field appearance-none outline-none w-full ${
                      errors.point_of_contact ? "error" : ""
                    }`}
                  />
                )}
              />
              {errors.point_of_contact && (
                <small className="error-message">
                  {errors.point_of_contact.message}
                </small>
              )}
            </div>

            <div className="field col-6">
              <label className="contact-label mt-2">
                Contact email {shareholderType === "Individual" && "*"}
              </label>
              <Controller
                name="contact_email"
                control={control}
                defaultValues
                rules={{
                  ...(shareholderType === "Individual"
                    ? { required: "Email is required." }
                    : {}),
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/,
                    message: "Email is invalid.",
                  },
                }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    type="text"
                    value={field.value}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setRegData({ ...regData, contact_email: newValue });
                      field.onChange(newValue);
                    }}
                    placeholder="Enter email address"
                    disabled={!isEditing}
                    className={`input-field appearance-none outline-none w-full ${
                      errors.contact_email ? "error" : ""
                    }`}
                  />
                )}
              />
              {errors.contact_email && (
                <small className="error-message">
                  {errors.contact_email.message}
                </small>
              )}
            </div>

            {shareholderType === "Individual" && (
              <>
                <div className="field col-12">
                  <p className="current-position-head mt-2">
                    Tax Identification Numbers
                  </p>
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">
                    Tax identification number (TIN)
                  </label>
                  <Controller
                    name="tax_identification_no"
                    control={control}
                    defaultValues
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({
                            ...regData,
                            tax_identification_no: newValue,
                          });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Enter tax ID number"
                        disabled={!isEditing}
                        className={`input-field appearance-none outline-none w-full`}
                      />
                    )}
                  />
                </div>

                <div className="field col-6">
                  <label className="contact-label mt-2">
                    Local tax ID number (if applicable)
                  </label>
                  <Controller
                    name="local_tax_id"
                    control={control}
                    defaultValues
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setRegData({ ...regData, local_tax_id: newValue });
                          field.onChange(newValue);
                        }}
                        type="text"
                        placeholder="Enter local tax ID number"
                        disabled={!isEditing}
                        className={`input-field appearance-none outline-none w-full`}
                      />
                    )}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </form>
  );
};

export default ShareholderInformationForm;
